import { appConfig } from "../../config/app.config"
import {
  BankResponse,
  CustomersResponse,
  KycResponse,
  GetAllClientsResponse,
  Client
} from "../types/customer.types";


const baseURL = appConfig.BASE_URL;

export const customersApi = {
  getAllUsers: async (page = 1, limit = 50): Promise<CustomersResponse> => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/users?page=${page}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

  

    if (!response.ok) {
      throw new Error(`Error fetching users: ${response.status} - ${(await response.json()).message}`);

    }
    return await response.json();
  },
  getAllClients: async (page = 1, limit = 50, search = ""): Promise<GetAllClientsResponse> => {
    const token = localStorage.getItem("access_token");
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/clients?page=${page}&limit=${limit}${searchParam}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error: any = new Error(body?.message || `Error fetching users: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  },

  searchClients: async (
    page = 1,
    limit = 50,
    filters: {
      email?: string;
      client_id?: string;
      email_verified?: string;
      status?: string;
      kyc_status?: string;
      created_at_from?: string;
      created_at_to?: string;
      updated_at_from?: string;
      updated_at_to?: string;
    } = {}
  ): Promise<GetAllClientsResponse> => {
    const token = localStorage.getItem("access_token");
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });

    if (filters.email)           params.set("email",            filters.email);
    if (filters.client_id)       params.set("client_id",        filters.client_id);
    if (filters.email_verified)  params.set("email_verified",   filters.email_verified);
    if (filters.status)          params.set("status",           filters.status);
    if (filters.kyc_status)      params.set("kyc_status",       filters.kyc_status);
    if (filters.created_at_from) params.set("created_at_from",  filters.created_at_from);
    if (filters.created_at_to)   params.set("created_at_to",    filters.created_at_to);
    if (filters.updated_at_from) params.set("updated_at_from",  filters.updated_at_from);
    if (filters.updated_at_to)   params.set("updated_at_to",    filters.updated_at_to);

    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/clients/search?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error: any = new Error(body?.message || `Error fetching clients: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  },

  createUser: async (email: string, mobile: string, clientId: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/user/register/${clientId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({ email, mobile }),
    });
  
    const data = await response.json();
  
    if (!response.ok) {
      // Throw with full info
      throw {
        status: response.status,
        message: data.message || 'Unknown error'
      };
    }
  
    return data;
  },
  
  createClient: async (email: string, password: string, name: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/client/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({ email, client_secret:password,name }),
      
    });

    if (!response.ok) {
      throw new Error(`Error creating client: ${response.status}`);
    }

    return await response.json();
  },
  createKyc: async (
    userId: string,
    clientId: string,
    kycData: Array<{
      document_type: string;
      document_name: string;
      document_number: string;
      dateOfBirth: string;
      Nationality: string;
      nameOnCard: string;
    }>
  ): Promise<any> => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/kyc/${clientId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({
        user_id: userId,
        kyc: kycData
      }),
    });

    if (!response.ok) {
      throw new Error(`Error creating KYC: ${response.status}`);
    }

    return await response.json();
  },

  createBank: async (
    userId: string,
    accountNumber: string,
    ifscCode: string
  ): Promise<any> => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/bank`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({
        user_id: userId,
        account_number: accountNumber,
        ifsc_code: ifscCode,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error creating bank details: ${response.status}`);
    }

    return await response.json();
  },

  getKycDetails: async (userId: string): Promise<KycResponse> => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/kyc/${userId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching KYC details: ${response.status}`);
    }

    return await response.json();
  },

  getBankDetails: async (userId: string): Promise<BankResponse> => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/bank/${userId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching bank details: ${response.status}`);
    }

    return await response.json();
  },

  getUsersByClientId: async (clientId: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
  
    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/client/users/${clientId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );
  
    const data = await response.json();
  
    // Don't throw on 404 — just return the parsed response
    return {
      statusCode: response.status,
      ...data,
    };
  
  },
  // make delete client by id
  deleteClientById: async (clientId: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/client/delete/${clientId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });

    if (!response.ok) {
      throw new Error(`Error deleting client: ${response.status}`);
    }

    return await response.json();
  },

  updateClient: async (clientId: string, clientData: Partial<Client>): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/client/update/${clientId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(clientData),
    });

    if (!response.ok) {
      throw new Error(`Error updating client: ${response.status}`);
    }

    return await response.json();
  },
  getClientByClientId: async (clientId: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/client/${clientId}`, {
      method: "GET", 
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      }
    });

    if (!response.ok) {
      throw new Error(`Error fetching client: ${response.status}`);
    }

    return await response.json();
  },
  deleteUserById: async (userId: string): Promise<any> => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseURL}${appConfig.API_VERSION}/user/delete/${userId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });

    if (!response.ok) {
      throw new Error(`Error deleting user: ${response.status}`);
    }

    return await response.json();
  },
  updateUser: async (
    clientId: string,
    userId: string,
    email: string,
    mobile: string
  ): Promise<any> => {
    const token = localStorage.getItem("access_token");
  
    const response = await fetch(
      `${baseURL}${appConfig.API_VERSION}/user/update/${clientId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ user_id: userId, email, mobile }),
      }
    );
  
    // Try parsing response body
    const responseData = await response.json();
    console.log("Response from update user:", responseData);
  
    if (!response.ok) {
      // Attach detailed backend message in thrown error
      const errorMessage = responseData?.message || `Error updating user: ${response.status}`;
      throw new Error(errorMessage);
    }
  
    return responseData;
  }
  
};